import { DataService } from '@/api'
import { generateOptions } from '@/lib/generic.utils'
import { make } from 'vuex-pathify'
import { cloneDeep } from 'lodash'
import * as apiTo from '@/modular-api'

// Add known modules to the state in order for vuex-pathify to generate actions, mutations, getters
const state = {
  dashboard: {
    main: {}
  },
  subscriptions: {
    main: {},
    price: {}
  },
  publishers: {
    main: {}
  },
  vendors: {
    main: {}
  },
  licenses: {
    main: {}
  },
  titlesMap: {
    'price-development-in-original-currency': {
      title: 'Price development in [[ CURRENCY ]]',
      subtitle: 'In original currency'
    },
    'price-development-in-local-currency': {
      title: 'Price development in [[ CURRENCY ]]',
      subtitle: 'In local currency'
    },
    'price-increase-in-percentage-in-original-currency-compared-to-average-price-increase': {
      title: 'Price increase in [[ CURRENCY ]]',
      subtitle: 'In percentage in original currency compared to average price increase'
    },
    'price-development-in-local-currency-compared-to-average-price': {
      title: 'Price development in [[ CURRENCY ]]',
      subtitle: 'In local currency compared to average price'
    },
    'price-vs-forecasted-price-original-currency': {
      title: 'Price vs Forecasted price in [[ CURRENCY ]]',
      subtitle: 'In original currency'
    },
    'renewal-status': {
      title: 'Renewal status [[ YEAR ]]',
      subtitle: null
    },
    'subscription-spend-overview': {
      title: 'Overview',
      subtitle: null
    },
    'total-spend': {
      title: 'Total spend',
      subtitle: null
    },
    'to-do-list': {
      title: 'Tasks',
      subtitle: null
    },
    reminders: {
      title: 'Cancellation reminders',
      subtitle: null
    }
  },
  layout: [],
  renewalStatusChartData: null
}

const mutations = {
  ...make.mutations(state),
  update(state, data) {
    if (!state) state = {}
    for (let i = 0; i < data.length; i++) {
      if (!state[data[i]['name']]) state[data[i]['name']] = {}
      data[i]['sub_contexts'].forEach(subcontext => {
        state[data[i]['name']][subcontext.name] = subcontext
      })
    }
  },
  updateSubcontext(state, data) {
    let cleanData = cloneDeep(data)
    delete cleanData.context
    cleanData.available_elements = generateOptions({
      collection: data.available_elements,
      valueKey: 'slug',
      additionalProperties: ['enabled']
    })
    state[data.context.name][data.name] = Object.assign({}, state[data.context.name][data.name], cleanData)
  },
  updateSubcontextData(state, payload) {
    state[payload.context][payload.subcontext]['data'] = Object.assign(
      {},
      state[payload.context][payload.subcontext]['data'],
      {}
    )
    state[payload.context][payload.subcontext]['data'][payload.name] = payload.data
  },
  setLayout: (state, payload) => (state.layout = payload),
  setDashboardLayout: (state, payload) => (state.layout[1].sub_contexts = payload),
  setSubscriptionLayout: (state, payload) => (state.layout[0].sub_contexts = payload),
  setRenewalStatusChartData: (state, payload) => (state.renewalStatusChartData = payload)
}

const actions = {
  ...make.actions(state),
  getContext({ commit, state }) {
    if (state.layout.length) return

    return apiTo.GET_LAYOUTS().then(({ data }) => {
      commit(`update`, data.result)
      commit(`setLayout`, data.result)
    })
  },
  getDefinition({ commit }) {
    const SERVICE = new DataService(`layouts`)
    return SERVICE.get().then(response => {
      commit(`updateSubcontext`, response.data.result)
    })
  },
  saveDefinition(context, payload) {
    const SERVICE = new DataService(`layouts/sub-contexts/${payload.id}/definition`)
    return SERVICE.update({ value: { definition: payload.definition } })
  },
  getChartDataForSpecificEntity({ commit }, payload) {
    const SERVICE = new DataService(`charts/${payload.chartName}/subscriptions/${payload.entityId}`)
    return new Promise(resolve => {
      return SERVICE.get().then(response => {
        commit('updateSubcontextData', {
          name: payload.chartName,
          context: payload.context,
          subcontext: payload.subcontext,
          data: response.data.result
        })
        resolve()
      })
    })
  },
  getChartData({ commit }, payload) {
    const SERVICE = new DataService(`charts/${payload.chartName}`)
    return new Promise(resolve => {
      return SERVICE.get().then(response => {
        if (payload.chartName === 'renewal-status') {
          commit('setRenewalStatusChartData', response.data.result)
        }
        commit('updateSubcontextData', {
          name: payload.chartName,
          context: payload.context,
          subcontext: payload.subcontext,
          data: response.data.result
        })
        resolve()
      })
    })
  },
  getInfoBoxData({ commit }, payload) {
    const SERVICE = new DataService(`info-boxes/${payload.sectionName}`)
    return new Promise(resolve => {
      return SERVICE.get().then(response => {
        commit('updateSubcontextData', {
          name: payload.sectionName,
          context: payload.context,
          subcontext: payload.subcontext,
          data: response.data.result
        })
        resolve()
      })
    })
  }
}

const getters = {
  ...make.getters(state),
  dashboardLayout(state) {
    if (!state.layout.length) return []
    const index = state.layout.findIndex(l => l.name === 'dashboard')
    return state.layout[index].sub_contexts
  },
  subscriptionLayout(state) {
    if (!state.layout.length) return []
    const index = state.layout.findIndex(l => l.name === 'subscriptions')
    return state.layout[index].sub_contexts
  },
  renewalStatusChartData: state => state.renewalStatusChartData
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
